/*
 * Copyright (C) 2026 - present by Potentially
 *
 * Please see distribution for license.
 */

import type { V3Styles } from '@app/app/shared/models/organization/organization.model';

export type V3StyleKey = 'brand' | 'selected' | 'background' | 'text' | 'border';

export type V3StyleValues = Record<V3StyleKey, string>;

export const V3_STYLE_KEYS: V3StyleKey[] = ['brand', 'selected', 'background', 'text', 'border'];

export const V3_STYLE_DEFAULTS: V3StyleValues = {
  brand: '#110F0D',
  selected: '#0F3A4D',
  background: '#F6F5F4',
  text: '#110F0D',
  border: '#E8E6E4',
};

export const HEX_COLOUR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface V3StyleVariable {
  property: string;
  defaultValue: string;
  weight: number;
}

const variable = (property: string, defaultValue: string, weight = 0): V3StyleVariable => ({ property, defaultValue, weight });

const STYLE_VARIABLES: Record<V3StyleKey, V3StyleVariable[]> = {
  brand: [
    variable('--accentSoft', 'var(--brand-ink-100)', 0.88),
    variable('--accentBdr', 'var(--brand-ink-300)', 0.62),
    variable('--accentMid', 'var(--brand-ink-600)', 0.12),
    variable('--accent', 'var(--brand-ink-900)'),
    variable('--accentDark', 'var(--brand-ink-700)', -0.32),
    variable('--color-blue-25', 'var(--brand-paper-deep)', 0.96),
    variable('--color-blue-50', 'var(--brand-ink-100)', 0.88),
    variable('--color-blue-100', 'var(--brand-ink-200)', 0.75),
    variable('--color-blue-200', 'var(--brand-ink-300)', 0.62),
    variable('--color-blue-300', 'var(--brand-ink-400)', 0.45),
    variable('--color-blue-400', 'var(--brand-ink-600)', 0.25),
    variable('--color-blue-500', 'var(--brand-ink-700)', 0.12),
    variable('--color-blue-600', 'var(--brand-ink-900)'),
    variable('--color-blue-700', 'var(--brand-ink-800)', -0.16),
    variable('--color-blue-800', 'var(--brand-ink-700)', -0.32),
    variable('--color-blue-900', 'var(--brand-ink-900)', -0.45),
    variable('--color-blue-950', 'var(--brand-ink-900)', -0.6),
  ],
  selected: [
    variable('--brand-deep', '#0F3A4D'),
    variable('--selected', 'var(--brand-deep)'),
    variable('--table-head', 'var(--brand-deep)'),
    variable('--table-head-label', 'var(--brand-deep)'),
    variable('--snake-line', 'var(--brand-deep)'),
    variable('--snake-node', 'var(--brand-deep)'),
    variable('--snake-node-done', 'var(--brand-deep)'),
  ],
  background: [variable('--bg', 'var(--brand-paper-deep)'), variable('--surfaceMuted', 'var(--brand-paper-deep)')],
  text: [
    variable('--ink', 'var(--brand-ink-900)'),
    variable('--text', 'var(--brand-ink-700)', 0.12),
    variable('--color-base-black', 'var(--brand-ink-900)'),
    variable('--color-coldgrey-600', 'var(--brand-ink-700)', 0.12),
    variable('--color-coldgrey-700', 'var(--brand-ink-700)', 0.12),
    variable('--color-coldgrey-800', 'var(--brand-ink-900)'),
    variable('--color-coldgrey-900', 'var(--brand-ink-900)'),
    variable('--color-coldgrey-950', 'var(--brand-ink-900)'),
  ],
  border: [variable('--rule', 'var(--brand-ink-200)'), variable('--color-coldgrey-200', 'var(--brand-ink-200)')],
};

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

function parseHex(hex: string): Rgb {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function shade(colour: Rgb, weight: number): Rgb {
  const target = weight < 0 ? BLACK : WHITE;
  const amount = Math.abs(weight);
  return {
    r: colour.r + (target.r - colour.r) * amount,
    g: colour.g + (target.g - colour.g) * amount,
    b: colour.b + (target.b - colour.b) * amount,
  };
}

export function normaliseV3Styles(styles: Partial<V3StyleValues> | null | undefined): V3StyleValues {
  return V3_STYLE_KEYS.reduce((values, key) => {
    const colour = styles?.[key]?.toUpperCase();
    values[key] = colour && HEX_COLOUR_PATTERN.test(colour) ? colour : V3_STYLE_DEFAULTS[key];
    return values;
  }, {} as V3StyleValues);
}

export function resolveV3StyleVariables(
  styles: Partial<V3StyleValues> | null | undefined,
  includeDefaults = false,
): Record<string, string> {
  const values = normaliseV3Styles(styles);
  const variables: Record<string, string> = {};
  for (const key of V3_STYLE_KEYS) {
    const isDefault = values[key] === V3_STYLE_DEFAULTS[key];
    if (isDefault && !includeDefaults) {
      continue;
    }
    const base = parseHex(values[key]);
    for (const { property, defaultValue, weight } of STYLE_VARIABLES[key]) {
      variables[property] = isDefault ? defaultValue : toHex(shade(base, weight));
    }
  }
  return variables;
}

export function toV3Styles(values: V3StyleValues): V3Styles {
  const normalised = normaliseV3Styles(values);
  return V3_STYLE_KEYS.reduce((styles, key) => {
    if (normalised[key] !== V3_STYLE_DEFAULTS[key]) {
      styles[key] = normalised[key];
    }
    return styles;
  }, {} as V3Styles);
}
